import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import styles from "./PlaceholderBundle.module.css";
import { SubStepType } from "../../Types";
import { PlaceholderBundleContext, ExtendedPlaceholderStepsType } from "./PlaceholderBundle";
import AngleLeftIcon from "../../../../components/Icons/AngleLeftIcon";
import ClickAwayListener from "../../BundlesComponents/ClickAwayListener/ClickAwayListener";
import AngleRightIcon from "../../../../components/Icons/AngleRightIcon";
import { CheckIcon } from "@shopify/polaris-icons";
import {
    checkForStepConditions,
    getCurrentPageSelectCount,
    getCurrentPageSelectTotal,
    renderErrorsOnButton,
} from "./PlaceholderConstants";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { BundleTypes } from "../../constants";

const SCROLL_STEP = 120; // px to scroll on button click

export const RenderSubStepBadges = () => {
    const { activeSubStep, setActiveSubStep, steps, activeStep, bundleConfig, setSearchQuery } =
        useContext(PlaceholderBundleContext);

    const subStepsContainerRef = useRef<HTMLDivElement>(null);

    return (
        <div className={styles.subStepsBadges} ref={subStepsContainerRef}>
            {steps[activeStep]?.subSteps.map((subStep: SubStepType, idx: number) => {
                return (
                    <div
                        className={`${styles.subStepBadge} ${idx == activeSubStep && styles.active}`}
                        onClick={() => {
                            setActiveSubStep(idx);
                            setSearchQuery("");
                        }}
                        key={subStep?.step_label + idx}
                        style={
                            bundleConfig?.bundle_type === BundleTypes.BB_PLACEHOLDER
                                ? {
                                      color: bundleConfig?.bundles_design?.add_to_cart_bg_color ?? "#303030",
                                      borderBottom:
                                          idx === activeSubStep
                                              ? `2px solid ${
                                                    bundleConfig?.bundles_design?.add_to_cart_bg_color ?? "#303030"
                                                }`
                                              : "",
                                  }
                                : {
                                      // @ts-expect-error required
                                      "--substep-bg-color": bundleConfig?.bundles_design?.substep_bg_color,
                                      "--substep-text-color": bundleConfig?.bundles_design?.substep_text_color,
                                  }
                        }
                    >
                        {subStep.step_label || "-"}
                    </div>
                );
            })}
        </div>
    );
};

export const RenderSubStepDropdown = ({
    subStepDropdownOpen,
    setSubStepDropdownOpen,
}: {
    subStepDropdownOpen: boolean;
    setSubStepDropdownOpen: Dispatch<SetStateAction<boolean>>;
}) => {
    const { steps, activeStep, activeSubStep, setActiveSubStep, bundleConfig } = useContext(PlaceholderBundleContext);
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        setShowDropdown(steps[activeStep]?.subSteps?.length > 1 ? true : false);
    }, [steps[activeStep]?.subSteps]);

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                minWidth: 200,
                maxWidth: 200,
                margin: "auto",
                marginBottom: 20,
            }}
        >
            <div
                className={`${styles.dropdownOptSelected}`}
                style={{
                    width: "100%",
                    // @ts-expect-error required
                    "--substep-bg-color": bundleConfig?.bundles_design?.substep_bg_color,
                    "--substep-text-color": bundleConfig?.bundles_design?.substep_text_color,
                    cursor: showDropdown ? "pointer" : "default",
                    fontSize: "14px",
                    fontWeight: "750",
                }}
                onClick={(e) => {
                    e?.stopPropagation();
                    e.preventDefault();
                    if (showDropdown) setSubStepDropdownOpen((ps) => !ps);
                }}
            >
                {steps[activeStep]?.subSteps[activeSubStep]?.step_label || "SubStep " + (+activeSubStep + 1)}
                {showDropdown && (
                    <span
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: subStepDropdownOpen ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <AngleLeftIcon color={"white"} size={".7rem"} />
                    </span>
                )}
            </div>
            {subStepDropdownOpen && (
                <ClickAwayListener onClickAway={() => setSubStepDropdownOpen(false)}>
                    <div className={styles.dropdownOptionsWrapper}>
                        {steps[activeStep]?.subSteps?.map((subStep: SubStepType, idx: number) => {
                            return (
                                <div
                                    className={`${styles.dropdownOpt} ${idx == activeSubStep && styles.activeOpt}`}
                                    key={idx + subStep?.step_label}
                                    onClick={(e) => {
                                        e?.stopPropagation();
                                        e.preventDefault();
                                        setActiveSubStep(idx);
                                        setSubStepDropdownOpen(false);
                                    }}
                                    style={{
                                        // @ts-expect-error required
                                        "--substep-bg-color": bundleConfig?.bundles_design?.substep_bg_color,
                                        "--substep-text-color": bundleConfig?.bundles_design?.substep_text_color,
                                        fontSize: "14px",
                                        fontWeight: "750",
                                    }}
                                >
                                    {subStep?.step_label || "SubStep " + (+idx + 1)}
                                </div>
                            );
                        })}
                    </div>
                </ClickAwayListener>
            )}
        </div>
    );
};

const StepsHeaderBar = () => {
    const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const { steps = [], activeStep, isMobile, bundleConfig, standAlone } = useContext(PlaceholderBundleContext);

    useEffect(() => {
        if (stepRefs.current[activeStep] && scrollContainerRef.current && standAlone) {
            const activeElement = stepRefs.current[activeStep];
            const container = scrollContainerRef.current;

            const activeLeft = activeElement.offsetLeft;
            const activeWidth = activeElement.offsetWidth;
            const containerWidth = container.offsetWidth;

            container.scrollTo({
                left: activeLeft - containerWidth / 2 + activeWidth / 2,
                behavior: "smooth",
            });
        }
    }, [activeStep]);

    useEffect(() => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            setIsOverflowing(container.scrollWidth > container.clientWidth);
        }
    }, [steps, isMobile, scrollContainerRef]);

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: -SCROLL_STEP,
                behavior: "smooth",
            });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: SCROLL_STEP,
                behavior: "smooth",
            });
        }
    };

    return (
        <div
            style={{
                borderBottom: "2px solid rgba(227, 230, 227, 1)",
                margin: "auto",
                marginBottom: 20,
                paddingBottom: isMobile ? "10px" : "20px",
                position: "relative",
                overflow: "hidden",
                maxWidth: isMobile ? "90%" : "90%",
                display: "flex",
                justifyContent: "center",
            }}
        >
            {isOverflowing && (
                <span
                    onClick={scrollLeft}
                    className={`${styles.arrowsForSlide} ${styles.leftSlideArrow}`}
                    style={{
                        boxShadow: `${bundleConfig?.bundles_design?.bundle_widget_bg} 3px 0 20px 10px`,
                        marginTop: isMobile ? 7 : 18,
                    }}
                >
                    <AngleLeftIcon size={12} color={"white"} />
                </span>
            )}
            <div
                ref={scrollContainerRef}
                className={`${styles.bbStepsWrapper}`}
                style={{
                    width: "min-content",
                    maxWidth: `min-content`,
                    display: "flex",
                    gap: 20,
                    overflowX: "auto",
                    scrollBehavior: "smooth",
                    minHeight: isMobile ? 90 : 90,
                    overflowY: "hidden",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none", // IE & Edge
                    paddingInline: 10,
                }}
            >
                {steps.map((step: ExtendedPlaceholderStepsType, index: number) => (
                    <div
                        key={index}
                        ref={(el) => (stepRefs.current[index] = el)}
                        className={`${styles.stepWrapper} ${activeStep > index ? styles.completedStep : ""} ${
                            activeStep == index ? styles.activeStep : ""
                        }`}
                        style={{
                            minWidth: isMobile ? 80 : 110,
                            flex: "0 0 auto",
                            textAlign: "center",
                            marginBottom: isMobile ? 55 : 30,
                        }}
                    >
                        <div className={styles.stepIconWrapper}>
                            {activeStep > index ? (
                                <CheckIcon />
                            ) : (
                                <span className={styles.stepIconImg}>
                                    {step?.step_icon?.originalSrc ? (
                                        <img
                                            src={step?.step_icon?.originalSrc}
                                            alt={step?.step_icon?.alt}
                                            style={{
                                                borderRadius: 0,
                                                width: "100%",
                                                objectFit: "contain",
                                                objectPosition: "center",
                                            }}
                                        />
                                    ) : (
                                        index + 1
                                    )}
                                </span>
                            )}
                        </div>
                        <span className={styles.stepMeta}>
                            <span
                                className={styles.stepTitle}
                                style={{
                                    width: isMobile ? "100%" : "max-content",
                                    textAlign: "center",
                                    color: bundleConfig?.bundles_design?.step_label_color,
                                }}
                            >
                                {step?.step_label}
                            </span>
                        </span>
                        {steps?.length - 1 != index && (
                            <span
                                className={`${styles.progressSlip} ${activeStep > index && styles.activeSlip}`}
                            ></span>
                        )}
                    </div>
                ))}
            </div>
            {isOverflowing && (
                <span
                    onClick={scrollRight}
                    className={`${styles.arrowsForSlide} ${styles.rightSlideArrow}`}
                    style={{
                        boxShadow: `${bundleConfig?.bundles_design?.bundle_widget_bg} -3px 0 20px 10px`,
                        marginTop: isMobile ? 7 : 18,
                    }}
                >
                    <AngleRightIcon size={12} color={"white"} />
                </span>
            )}
        </div>
    );
};

export const StepsHeaderTabs = () => {
    const { steps = [], activeStep, setActiveStep, bundleConfig } = useContext(PlaceholderBundleContext);

    const handleStepClick = (targetIndex: number) => {
        setActiveStep(targetIndex);
    };

    const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const activeStepEl = stepRefs.current[activeStep];
        if (activeStepEl) {
            activeStepEl.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest",
            });
        }
    }, [activeStep]);

    return (
        <div
            style={{
                borderBottom: "2px solid rgba(227, 230, 227, 1)",
                display: "flex",
                justifyContent: "center",
            }}
        >
            <div className={styles.stepHeaders}>
                {steps?.map((step: ExtendedPlaceholderStepsType, idx: number) => {
                    const isActive = activeStep === idx;
                    return (
                        <div
                            key={idx}
                            ref={(el) => (stepRefs.current[idx] = el)}
                            style={{
                                backgroundColor: isActive ? "#282828" : undefined,
                                cursor: "pointer",
                            }}
                            className={styles.stepHeaderChild}
                            onClick={() => handleStepClick(idx)}
                        >
                            <div
                                style={{
                                    color: isActive ? "#ffffff" : bundleConfig?.bundles_design?.step_label_color,
                                    padding: "3px 20px",
                                    wordBreak: "keep-all",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {step?.step_label}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default StepsHeaderBar;
